import React, {useRef, useState} from "react";
import {Delete, UploadFile} from "@mui/icons-material";
import {toast} from "react-toastify";
import {Card, CardContent, Grid} from "@mui/material";
import IconButton from "@mui/material/IconButton";

type DragAndDropFileProps = {
    onFileUpload: (files: File[]) => void;
    maxSize?: number;
    types?: string[];
    multiple?: boolean;
    loading?: boolean;
};

const DragAndDropFileComponent: React.FC<DragAndDropFileProps> = (
    {
        onFileUpload,
        maxSize = 30,
        types = ['pdf'],
        multiple = false,
        loading = false,
    }
) => {
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

    const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);

        const files = Array.from(event.dataTransfer.files);
        if (files.length > 0) {
            const allowedFiles = getAllowedFiles(files);
            onFileUpload(allowedFiles);
            setSelectedFiles(allowedFiles);
        }
    };

    const getAllowedFiles = (files: File[]) => {
        const invalidCharacters = /[@%&*#]/
        let allowedFiles = files.filter(file => /^[^.]+\.[^.]+$/.test(file.name) && !invalidCharacters.test(file.name));
        if (allowedFiles.length != files.length) {
            toast.error('Nome do arquivo inválido. Não é permitido o uso de pontos extras ou caracteres especiais, como "@, %, &, *, #".');
            if (allowedFiles.length === 0) {
                return [];
            }
        }
        allowedFiles = allowedFiles.filter(file => types.includes(file.name.split('.').pop() || ''));
        if (allowedFiles.length === 0) {
            toast.error(`Arquivo inválido. Tipos permitidos: ${types.join(', ')}`);
            return [];
        } else {
            allowedFiles = allowedFiles.filter(file => file.size / 1024 / 1024 <= maxSize);
            if (allowedFiles.length === 0) {
                toast.error(`Arquivo inválido. Tamanho máximo permitido: ${maxSize}MB`);
                return [];
            }
        }
        if (!multiple && allowedFiles.length > 1) {
            toast.error(`Apenas um arquivo é permitido. Foram selecionados ${allowedFiles.length} arquivos.`);
        }
        return multiple ? allowedFiles : [allowedFiles[0]];
    }

    const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        if (files.length > 0) {
            const allowedFiles = getAllowedFiles(files);
            onFileUpload(allowedFiles);
            setSelectedFiles(allowedFiles);
        }
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleDeleteFile = (file: File) => {
        setSelectedFiles(selectedFiles.filter(f => f !== file));
        onFileUpload(selectedFiles.filter(f => f !== file));
    }

    return !selectedFiles.length ? (
        <div
            style={{
                border: `2px dashed ${isDragging ? "rgba(57,89,249,0.56)" : "#ddd"}`,
                borderRadius: "8px",
                padding: "20px",
                textAlign: "center",
                backgroundColor: isDragging ? "rgba(57,89,249,0.1)" : "#fff",
                transition: "background-color 0.3s",
            }}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            <div>
                <UploadFile style={{
                    color: '#3959f9',
                }}/>
                <p style={{
                    margin: '0.4rem',
                    textDecoration: 'underline',
                    color: '#3959f9',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                }}
                   onClick={handleClick}
                >
                    Clique aqui para adicionar o arquivo.
                </p>
                <p style={{margin: '0.4rem', color: '#727272'}}>{types.join(', ').toUpperCase()} (max. {maxSize}MB)</p>
            </div>
            <input
                type="file"
                multiple={multiple}
                style={{display: "none"}}
                ref={fileInputRef}
                accept={types.map(type => `.${type}`).join(',')}
                onChange={handleFileSelection}
            />
        </div>
    ) : (
        <>
            {selectedFiles.map((file, index) => (
                <Card sx={{width: "100%", mb: "0.5rem"}} key={`${file.name}-${index}`}>
                    <CardContent sx={{height: '100%', paddingBottom: '16px'}}>
                        <Grid container spacing={2}>
                            <Grid  size={1} display="flex" alignItems="center">
                                <UploadFile style={{
                                    color: '#3959f9',
                                }}/>
                            </Grid>
                            <Grid  size={7}>
                                <p style={{margin: '0.1rem'}}>{file.name}</p>
                                <p style={{margin: '0.1rem', color: '#727272'}}>
                                    {(file.size / 1024 / 1024).toFixed(2)}MB
                                </p>
                            </Grid>
                            <Grid  size={4} display="flex" alignItems="center" justifyContent="end">
                                <IconButton
                                    className="hover-icon"
                                    onClick={() => handleDeleteFile(file)}
                                    sx={{
                                        color: '#727272'
                                    }}
                                    disabled={loading}
                                >
                                    <Delete style={{color: '#727272'}}/>
                                </IconButton>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            ))}
        </>
    )
};

export default DragAndDropFileComponent;
